import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

// Brand-accent button text: secondary brand color in light mode, primary
// brand color in dark mode (both driven by the settings-backed CSS vars set
// in BrandContext.jsx, with static fallbacks in index.css). Applied to every
// variant except default/secondary, whose background already IS one of
// those brand colors — overriding their text too would make them unreadable.
const BRAND_TEXT = "text-[var(--brand-secondary-accent)] dark:text-[var(--brand-accent)]";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          `bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 ${BRAND_TEXT}`,
        outline:
          `border border-input shadow-sm hover:bg-accent hover:text-accent-foreground ${BRAND_TEXT}`,
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: `hover:bg-accent hover:text-accent-foreground ${BRAND_TEXT}`,
        link: `underline-offset-4 hover:underline ${BRAND_TEXT}`,
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
